import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { adminsService } from '../data/admins.service';
import { getApiErrorMessage } from '@/shared/api/error-code-mapping';
import type { CreateAdminRequest, UpdateAdminRequest } from '../types/admin.types';

export const useAdminActions = () => {
    const queryClient = useQueryClient();

    const createAdmin = useMutation({
        mutationFn: (values: CreateAdminRequest) => adminsService.createAdmin(values),
        onSuccess: () => {
            message.success('Admin created successfully');
            queryClient.invalidateQueries({ queryKey: ['admins'] });
        },
        onError: (err: any) => message.error(getApiErrorMessage(err, 'Failed to create admin'))
    });

    const updateAdmin = useMutation({
        mutationFn: ({ id, values }: { id: string, values: UpdateAdminRequest }) => 
            adminsService.updateAdmin(id, values),
        onSuccess: () => {
            message.success('Admin updated successfully');
            queryClient.invalidateQueries({ queryKey: ['admins'] });
        },
        onError: (err: any) => message.error(getApiErrorMessage(err, 'Failed to update admin'))
    });

    const deleteAdmin = useMutation({
        mutationFn: (id: string) => adminsService.deleteAdmin(id),
        onSuccess: () => {
            message.success('Admin deleted');
            queryClient.invalidateQueries({ queryKey: ['admins'] });
        },
        onError: (err: any) => message.error(getApiErrorMessage(err, 'Failed to delete admin'))
    });

    const disableAdmin = useMutation({
        mutationFn: (id: string) => adminsService.disableAdmin(id),
        onSuccess: () => {
            message.success('Admin disabled');
            queryClient.invalidateQueries({ queryKey: ['admins'] });
        }
    });

    const enableAdmin = useMutation({
        mutationFn: (id: string) => adminsService.enableAdmin(id),
        onSuccess: () => {
            message.success('Admin enabled');
            queryClient.invalidateQueries({ queryKey: ['admins'] });
        }
    });

    const resetMfa = useMutation({
        mutationFn: ({ id, reason }: { id: string, reason: string }) => 
            adminsService.resetMfa(id, reason),
        onSuccess: () => {
            message.success('MFA reset successfully');
            queryClient.invalidateQueries({ queryKey: ['admins'] });
        },
        onError: (err: any) => message.error(getApiErrorMessage(err, 'Failed to reset MFA'))
    });

    const addWhitelistedIp = useMutation({
        mutationFn: (values: { ip_address: string, label?: string }) => 
            adminsService.addWhitelistedIp(values),
        onSuccess: () => {
            message.success('IP added');
            queryClient.invalidateQueries({ queryKey: ['whitelisted_ips'] });
        },
        onError: (err: any) => message.error(getApiErrorMessage(err, 'Failed to add IP'))
    });

    const removeWhitelistedIp = useMutation({
        mutationFn: (id: number) => adminsService.removeWhitelistedIp(id),
        onSuccess: () => {
            message.success('IP removed');
            queryClient.invalidateQueries({ queryKey: ['whitelisted_ips'] });
        }
    });

    return {
        createAdmin,
        updateAdmin,
        deleteAdmin,
        disableAdmin,
        enableAdmin,
        resetMfa,
        addWhitelistedIp,
        removeWhitelistedIp
    };
};
